import { usePathname, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import AlreadyHaveAccount from "../components/AlreadyHaveAccount";
import { AutocompleteItem } from "../components/AutocompleteItem";
import LocationDisclaimerSheet from "../components/Modal/LocationDisclaimerSheet";
import { useAppSelector } from "../hooks";

import {
  type AutoCompleteResponseFragment,
  isAndroid,
  useDebounce,
  useGetAutoCompleteAddressQuery,
  useValidateAddressInLocalizationMutation,
} from "@stepapp/core";
import {
  Block,
  Input,
  Loader,
  SafeAreaView,
  SvgIcon,
  Touchable,
  palette,
  spacing,
} from "@stepapp/ui";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";

export default function AddressAutocompleteScreen() {
  const { t } = useTranslation();
  const { back } = useRouter();
  const path = usePathname();
  const { localizationId, city } = useAppSelector(({ appConfig }) => appConfig);
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);

  const { setValue, watch } = useFormContext();
  const address = watch("address");
  const isReservation = useMemo(() => path.includes("service"), [path]);

  const [selectedAddress, setSelectedAddress] = useState(address?.streetAddress1 || "");

  const debouncedAddress = useDebounce(selectedAddress, 500);

  const [validateAddress] = useValidateAddressInLocalizationMutation();
  const { data, loading } = useGetAutoCompleteAddressQuery({
    variables: { payload: { address: `${city}, ${debouncedAddress}` } },
    skip: debouncedAddress.length < 3,
  });

  const addresses = useMemo(
    () => data?.generateAutocompleteForAddress.slice(0, 3) || [],
    [data?.generateAutocompleteForAddress],
  );

  const showLogIn = isReservation && addresses.length > 0;

  const handleAddressPress = useCallback(
    async (autocompleteAddress: AutoCompleteResponseFragment) => {
      const { data } = await validateAddress({
        variables: {
          payload: {
            city: autocompleteAddress?.city || "",
            localizationId,
            postalOrZipCode: autocompleteAddress?.postalCode || "",
            streetAddress1: autocompleteAddress?.address || "",
            streetAddress2: "",
          },
        },
      });

      if (!data?.validateAddressInLocalization) {
        setSelectedAddress("");
        return setNotAvailableLocation(true);
      }

      setValue("address.postalOrZipCode", autocompleteAddress?.postalCode || "");
      setValue("address.streetAddress1", autocompleteAddress?.address || "");
      setValue("address.city", autocompleteAddress?.city || city || "");
      setSelectedAddress(autocompleteAddress?.address || "");
      back();
    },
    [city, localizationId, back, setValue, validateAddress],
  );

  return (
    <SafeAreaView edges={["bottom"]}>
      <Block paddingHorizontal={isReservation ? 0 : 2.5} paddingVertical={2.5}>
        {!isAndroid && (
          <Touchable onPress={() => back()}>
            <Block alignCenter justifyCenter style={styles.backIcon}>
              <SvgIcon color={palette.common.black} name={"chevron-left"} size={28} />
            </Block>
          </Touchable>
        )}

        <Block paddingTop={isAndroid ? 0 : 2.5}>
          <Input
            onChangeText={(v) => setSelectedAddress(v)}
            placeholder={t("components.input.placeholder.typeAddress")}
            value={selectedAddress}
          />
        </Block>

        {loading ? (
          <Block alignCenter paddingTop={2}>
            <Loader />
          </Block>
        ) : (
          <>
            {addresses.map((address, index) => (
              <AutocompleteItem
                {...address}
                divider={index !== addresses.length - 1}
                key={address.latitude}
                onPress={() => handleAddressPress(address)}
              />
            ))}
            {showLogIn && <AlreadyHaveAccount />}
          </>
        )}
      </Block>

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  backIcon: {
    backgroundColor: palette.secondary.grey,
    borderRadius: spacing.unit * 2.5,
    height: spacing.unit * 5,
    width: spacing.unit * 5,
  },
});
